import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { TotalPrice } from '@/components/TotalPrice/TotalPrice'
import { useBankIdContext } from '@/services/bankId/BankIdContext'
import { type ProductOfferFragment } from '@/services/graphql/generated'
import { convertToDate } from '@/utils/date'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useFormatter } from '@/utils/useFormatter'
import { datadogRum } from '@datadog/browser-rum'
import { Alert } from 'ui/src/components/Alert/Alert'
import { Button } from 'ui/src/components/Button/Button'
import { Divider } from 'ui/src/components/Divider/Divider'
import { BankIdIcon } from 'ui/src/icons/BankIdIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { useCarExtensionOffers } from './CarTrialExtensionOffers/CarTrialExtensionOffers'
import { PriceBreakdown } from './PriceBreakdown/PriceBreakdown'
import { type TrialContract } from './carDealership.types'
import { ConfirmPayWithoutExtensionButton } from './ConfirmPayWithoutExtensionButton'

type Props = {
  defaultOffer?: ProductOfferFragment
  trialContract: TrialContract
  shopSessionId: string
  ssn?: string
}

export const PayForTrial = ({ defaultOffer, trialContract, shopSessionId, ssn }: Props) => {
  const router = useRouter()
  const { t } = useTranslation('carDealership')
  const formatter = useFormatter()
  const locale = useRoutingLocale()
  const { startLogin } = useBankIdContext()
  const {
    selectedOffer,
    extensionActivationDate,
    isExtendingAfterConnectingPayment,
    isSigningWithoutExtension,
    isSigningWithExtension,
    isLoading,
    acceptSelectedExtensionOffer,
  } = useCarExtensionOffers()

  const handleSigningWithoutExtension = () => {
    datadogRum.addAction('Car dealership | Decline extension offer')

    if (!ssn) {
      throw new Error('Must have customer ssn')
    }

    startLogin({
      ssn,
      onSuccess() {
        console.log('Car dealership | BankID login success')
        const nextUrl = PageLink.carDealershipConfirmation({
          locale,
          contractId: trialContract.id,
        }).pathname
        router.push(PageLink.checkoutPaymentTrustly({ locale, shopSessionId: shopSessionId, nextUrl }).href)
      },
    })
  }

  const extendTrialWithSelectedOffer = () => {
    datadogRum.addAction('Car dealership | Click Sign')
    acceptSelectedExtensionOffer()
  }

  const trialTerminationDate = convertToDate(trialContract.terminationDate)
  if (!trialTerminationDate) {
    throw new Error(`Unable to parse terminationDate: ${trialContract.terminationDate}`)
  }

  const crossedOutAmount = defaultOffer?.cost.net

  // Extension flow
  if (isExtendingAfterConnectingPayment) {
    return (
      <div className={yStack({ gap: 'xl' })}>
        <TotalPrice amount={selectedOffer.cost.net.amount} currencyCode={selectedOffer.cost.net.currencyCode} />

        <div className={yStack({ gap: 'xs' })}>
          <Alert.Root variant="info">
            <Alert.Icon icon={InfoIcon} />
            <Alert.Message>{t('CAR_TRIAL_INSURANCE_RESPONSIBILITY_ALERT')}</Alert.Message>
          </Alert.Root>
          <Button size="large" onClick={extendTrialWithSelectedOffer} loading={isLoading} fullWidth>
            <div className={xStack({ gap: 'xs', alignItems: 'center' })}>
              <BankIdIcon /> {t('SIGN_BUTTON')}
            </div>
          </Button>
        </div>
      </div>
    )
  }

  // Payment connection flow
  return (
    <div className={yStack({ gap: 'lg' })}>
      <div className={yStack({ gap: 'sm' })}>
        {/* Always show the trial price breakdown in the payment connection flow */}
        <PriceBreakdown
          amount={trialContract.premium.amount}
          crossedOutAmount={crossedOutAmount?.amount}
          currencyCode={trialContract.premium.currencyCode}
          title={t('TRIAL_TITLE')}
          subTitle={trialContract.productVariant.displayNameSubtype}
          priceExplanation={t('TRIAL_COST_EXPLANATION', {
            date: formatter.dateFull(trialTerminationDate, {
              hideYear: true,
              abbreviateMonth: true,
            }),
          })}
        />

        {isSigningWithExtension && (
          <>
            <Divider />

            <PriceBreakdown
              amount={selectedOffer.cost.net.amount}
              currencyCode={selectedOffer.cost.net.currencyCode}
              title={t('EXTENSION_TITLE')}
              subTitle={selectedOffer.variant.displayNameSubtype}
              priceExplanation={t('EXTENSION_COST_EXPLANATION', {
                date: formatter.dateFull(extensionActivationDate, {
                  hideYear: true,
                  abbreviateMonth: true,
                }),
              })}
            />
          </>
        )}
      </div>

      <div className={yStack({ gap: 'xs' })}>
        <Alert.Root variant="info">
          <Alert.Icon icon={InfoIcon} />
          <Alert.Message>{t('CAR_TRIAL_INSURANCE_RESPONSIBILITY_ALERT')}</Alert.Message>
        </Alert.Root>

        {isSigningWithoutExtension && <ConfirmPayWithoutExtensionButton onConfirm={handleSigningWithoutExtension} />}
        {isSigningWithExtension && (
          <Button size="large" onClick={extendTrialWithSelectedOffer} loading={isLoading} fullWidth>
            <div className={xStack({ gap: 'xs', alignItems: 'center' })}>
              <BankIdIcon /> {t('SIGN_AND_PAY_BUTTON')}
            </div>
          </Button>
        )}
      </div>
    </div>
  )
}
